/**
 * TrustSection.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경] B2C 개인고객 전용 신뢰 섹션
 * 일반인이 중고기기 매입 시 가장 걱정하는 3가지를 해소:
 * 개인정보 안전 / 무료 방문 수거 / 최고가 보장 입금
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React from 'react';
import { Box, Grid, Typography, Card, CardContent } from '@mui/material';
import { VerifiedUser, LocalShipping, Payments } from '@mui/icons-material';
import { motion } from 'framer-motion';

const trustItems = [
  {
    icon: <VerifiedUser sx={{ fontSize: 32, color: '#fff' }} />,
    title: '개인정보 100% 안전',
    description: '공장 초기화 + 전문 데이터 삭제로\n개인정보를 완벽히 보호합니다',
    gradient: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    shadowColor: 'rgba(16, 185, 129, 0.25)',
  },
  {
    icon: <LocalShipping sx={{ fontSize: 32, color: '#fff' }} />,
    title: '무료 방문 수거',
    description: '택배 기사가 원하는 시간에 방문합니다.\n포장도 필요 없어요',
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 100%)',
    shadowColor: 'rgba(14, 165, 233, 0.25)',
  },
  {
    icon: <Payments sx={{ fontSize: 32, color: '#fff' }} />,
    title: '최고가 보장 입금',
    description: '검수 완료 후 1~2 영업일 내\n계좌로 입금됩니다',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    shadowColor: 'rgba(245, 158, 11, 0.25)',
  },
];

const TrustSection: React.FC = () => (
  <Box
    sx={{
      py: { xs: 4, md: 12 },
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    }}
  >
    <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.2rem', md: '2.2rem' },
            color: '#0f172a',
            mb: 1.5,
            letterSpacing: '-0.02em',
          }}
        >
          왜 다원트레이드일까요?
        </Typography>
        <Typography
          variant="body1"
          align="center"
          sx={{ color: '#64748b', mb: { xs: 2.5, md: 7 }, fontSize: { xs: '0.85rem', md: '1.05rem' } }}
        >
          안심하고 맡길 수 있는 3가지 이유
        </Typography>
      </motion.div>

      <Grid container spacing={{ xs: 1.5, md: 4 }}>
        {trustItems.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
              whileHover={{ y: -8 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 16px 48px ${item.shadowColor}`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: item.gradient,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 4 }, textAlign: { xs: 'left', md: 'center' } }}>
                  <Box sx={{ display: { xs: 'flex', md: 'block' }, alignItems: 'center', gap: { xs: 2, md: 0 } }}>
                    <Box
                      sx={{
                        width: { xs: 48, md: 68 },
                        height: { xs: 48, md: 68 },
                        borderRadius: '50%',
                        background: item.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: { xs: 0, md: 'auto' },
                        mb: { xs: 0, md: 3 },
                        flexShrink: 0,
                        boxShadow: `0 8px 24px ${item.shadowColor}`,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#0f172a',
                          mb: { xs: 0.3, md: 1.5 },
                          fontSize: { xs: '0.95rem', md: '1.2rem' },
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-line',
                          fontSize: { xs: '0.78rem', md: '0.9rem' },
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Box>
);

export default TrustSection;
